const COUNTRY_COLORS = [
    '#1E78B4', '#FF8F1F', '#2CA02C', '#D62728',
    '#9467BD', '#BCBD22', '#17BECF', '#2F4050'
  ];
  
  export function transformGlobalDistribution(apiData) {
    return {
      total: apiData.total,
      distribution: apiData.distribution.map((item, index) => ({
        name: item.country,
        value: parseFloat(item.percentage),
        valuex: parseInt(item.device_count),
        color: COUNTRY_COLORS[index % COUNTRY_COLORS.length]
      }))
    };
  }
  
  export function transformMonthlyInsects(apiData) {
    const years = apiData.yearLabels || Object.keys(apiData.years).map(Number);
    
    return apiData.months.map((month, index) => {
      const monthData = { month };
      years.forEach((year, yearIndex) => {
        monthData[`y${year}`] = apiData.years[year]?.[index] || 0;
      });
      return monthData;
    });
  }
  
  export function transformYearlySpecies(apiData) {
    const years = apiData.yearLabels || Object.keys(Object.values(apiData.years)[0] || {}).map(Number);
    
    return apiData.species.map(speciesName => {
      const speciesData = { species: speciesName };
      years.forEach((year) => {
        speciesData[`y${year}`] = apiData.years[speciesName]?.[year] || 0;
      });
      return speciesData;
    });
  }

// Transform summary devices data
export function transformSummaryDevices(apiData) {
  return apiData.map(device => ({
    id: device.id,
    global: device.global || 'N/A',
    region: device.region || 'N/A',
    client: device.client || 'N/A',
    address: device.address || 'N/A',
    location: device.location || 'N/A',
    deviceName: device.device_name || 'N/A',
    deviceId: device.device_id || 'N/A',
    startDate: device.start_date ? new Date(device.start_date).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    }).replace(/ /g, '-') : 'N/A',
    activity: device.activity || 'TBD',
    status: device.status || 'UNKNOWN',
    total: device.total || 0,
    top3: device.top3 || [],
    allSpecies: device.allSpecies || [] // Add this line
  }));
}

// Transform Monthly Total Insects Trapped data
export function transformMonthlyTotalInsects(apiData) {
  const { months, speciesKeys, dataMap } = apiData;
  
  // Colors for species (same as mock)
  const colorPalette = [
    '#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd',
    '#8c564b', '#e377c2', '#7f7f7f', '#bcbd22', '#17becf'
  ];
  
  const colors = {};
  speciesKeys.forEach((species, index) => {
    colors[species] = colorPalette[index % colorPalette.length];
  });

  // Build rows for chart
  const rowsForChart = months.map(monthLabel => {
    const row = { label: monthLabel };
    speciesKeys.forEach(species => {
      row[species] = dataMap[monthLabel]?.[species] || 0;
    });
    return row;
  });

  // Calculate totals by species
  const totalsBySpecies = {};
  speciesKeys.forEach(species => {
    totalsBySpecies[species] = months.reduce((sum, monthLabel) => {
      return sum + (dataMap[monthLabel]?.[species] || 0);
    }, 0);
  });

  // Calculate totals by month
  const totalsByMonth = months.map(monthLabel => {
    const total = speciesKeys.reduce((sum, species) => {
      return sum + (dataMap[monthLabel]?.[species] || 0);
    }, 0);
    return { month: monthLabel, total };
  });

  return {
    kind: 'TOTAL_BY_CLIENT',
    months: months.map(label => ({ ym: label, label })),
    seriesKeys: speciesKeys,
    rowsForChart,
    totalsBySpecies,
    totalsByMonth,
    colors
  };
}

// Transform Monthly Capture Trend Analysis data
export function transformMonthlyCaptureTrend(apiData) {
  const { months, speciesKeys, dataMap, threshold } = apiData;
  
  // Colors for species
  const colorPalette = [
    '#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd',
    '#8c564b', '#e377c2', '#7f7f7f', '#bcbd22', '#17becf'
  ];
  
  const colors = {};
  speciesKeys.forEach((species, index) => {
    colors[species] = colorPalette[index % colorPalette.length];
  });

  // Build rows for chart with Total and Threshold
  const rowsForChart = months.map(monthLabel => {
    const row = { label: monthLabel };
    let sum = 0;
    
    speciesKeys.forEach(species => {
      const value = dataMap[monthLabel]?.[species] || 0;
      row[species] = value;
      sum += value;
    });
    
    row.Total = sum;
    row.Threshold = threshold;
    return row;
  });

  // Calculate totals by species
  const totalsBySpecies = {};
  speciesKeys.forEach(species => {
    totalsBySpecies[species] = months.reduce((sum, monthLabel) => {
      return sum + (dataMap[monthLabel]?.[species] || 0);
    }, 0);
  });

  // Calculate totals by month
  const totalsByMonth = months.map(monthLabel => {
    const total = speciesKeys.reduce((sum, species) => {
      return sum + (dataMap[monthLabel]?.[species] || 0);
    }, 0);
    return { month: monthLabel, total };
  });

  // Threshold by month (constant value)
  const thresholdByMonth = months.map(monthLabel => ({
    month: monthLabel,
    value: threshold
  }));

  return {
    kind: 'CAPTURE_TREND_BY_AREA',
    months: months.map(label => ({ ym: label, label })),
    seriesKeys: speciesKeys,
    rowsForChart,
    totalsBySpecies,
    totalsByMonth,
    thresholdByMonth,
    colors
  };
}

// Transform Species Identified Analysis data
export function transformSpeciesIdentifiedByLocation(apiData) {
  if (!apiData || !apiData.areas || !apiData.speciesKeys || !apiData.dataMap) {
    console.error('Invalid API data:', apiData);
    return {
      kind: 'SPECIES_IDENTIFIED_BY_LOCATION',
      areas: [],
      speciesKeys: [],
      rows: [],
      totalsBySpecies: {},
      totalsByArea: [],
      colors: {}
    };
  }

  const { areas, speciesKeys, dataMap } = apiData;
  
  // Colors for species
  const colorPalette = [
    '#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd',
    '#8c564b', '#e377c2', '#7f7f7f', '#bcbd22', '#17becf'
  ];
  
  const colors = {};
  speciesKeys.forEach((species, index) => {
    colors[species] = colorPalette[index % colorPalette.length];
  });

  // Build rows for chart and table
  const rows = areas.map(areaName => {
    const row = { Area: areaName };
    speciesKeys.forEach(species => {
      row[species] = dataMap[areaName]?.[species] || 0;
    });
    return row;
  });

  // Calculate totals by species (for footer)
  const totalsBySpecies = {};
  speciesKeys.forEach(species => {
    totalsBySpecies[species] = areas.reduce((sum, areaName) => {
      return sum + (dataMap[areaName]?.[species] || 0);
    }, 0);
  });

  // Calculate totals by area (for last column)
  const totalsByArea = areas.map(areaName => {
    const total = speciesKeys.reduce((sum, species) => {
      return sum + (dataMap[areaName]?.[species] || 0);
    }, 0);
    return { area: areaName, total };
  });

  return {
    kind: 'SPECIES_IDENTIFIED_BY_LOCATION',
    areas,
    speciesKeys,
    rows,
    totalsBySpecies,
    totalsByArea,
    colors
  };
}

// Transform Monthly Species Trend Analysis data
export function transformSpeciesTrendByLocation(apiData) {
  if (!apiData || !apiData.months || !apiData.locationKeys || !apiData.dataMap) {
    console.error('Invalid API data:', apiData);
    return {
      kind: 'SPECIES_TREND_BY_LOCATION',
      months: [],
      locationKeys: [],
      rowsForChart: [],
      totalsByMonth: [],
      totalsByLocation: {},
      colors: {}
    };
  }

  const { months, locationKeys, dataMap } = apiData;
  
  // Colors for locations (match the screenshot)
  const colorPalette = [
    '#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd',
    '#8c564b', '#e377c2', '#7f7f7f', '#bcbd22', '#17becf',
    '#aec7e8', '#ffbb78', '#98df8a', '#ff9896', '#c5b0d5'
  ];
  
  const colors = {};
  locationKeys.forEach((location, index) => {
    colors[location] = colorPalette[index % colorPalette.length];
  });

  // Build rows for chart
  const rowsForChart = months.map(monthLabel => {
    const row = { label: monthLabel };
    locationKeys.forEach(location => {
      row[location] = dataMap[monthLabel]?.[location] || 0;
    });
    return row;
  });

  // Calculate totals by month
  const totalsByMonth = months.map(monthLabel => {
    const total = locationKeys.reduce((sum, location) => {
      return sum + (dataMap[monthLabel]?.[location] || 0);
    }, 0);
    return { month: monthLabel, total };
  });

  // Calculate totals by location
  const totalsByLocation = {};
  locationKeys.forEach(location => {
    totalsByLocation[location] = months.reduce((sum, monthLabel) => {
      return sum + (dataMap[monthLabel]?.[location] || 0);
    }, 0);
  });

  return {
    kind: 'SPECIES_TREND_BY_LOCATION',
    months: months.map(label => ({ ym: label, label })),
    locationKeys,
    rowsForChart,
    totalsByMonth,
    totalsByLocation,
    colors
  };
}